import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

// Define a storage interface to keep packages decoupled
export interface StorageProvider {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
}

interface EmployeeModeContextType {
  selectedEmployeeId: string | undefined;
  setSelectedEmployeeId: (id: string | undefined) => void;
}

const EmployeeModeContext = createContext<EmployeeModeContextType | undefined>(
  undefined
);

const STORAGE_KEY = 'selectedEmployeeId';

interface EmployeeModeProviderProps {
  children: ReactNode;
  storage?: StorageProvider;
}

export const EmployeeModeProvider: React.FC<EmployeeModeProviderProps> = ({
  children,
  storage,
}) => {
  const [selectedEmployeeId, setSelectedEmployeeIdState] = useState<
    string | undefined
  >(undefined);
  const [isInitialized, setIsInitialized] = useState(storage ? false : true);

  // Load initial value from storage
  useEffect(() => {
    const loadInitialValue = async () => {
      if (storage) {
        try {
          const storedValue = await storage.getItem(STORAGE_KEY);
          if (storedValue) {
            setSelectedEmployeeIdState(storedValue);
          }
        } finally {
          setIsInitialized(true);
        }
      }
    };

    loadInitialValue();
  }, [storage]);

  // Wrapped setter that also updates storage
  const setSelectedEmployeeId = useCallback(
    async (id: string | undefined) => {
      setSelectedEmployeeIdState(id);

      if (storage) {
        if (id) {
          await storage.setItem(STORAGE_KEY, id);
        } else {
          await storage.removeItem(STORAGE_KEY);
        }
      }
    },
    [storage]
  );

  // Only render children after we've initialized from storage
  if (!isInitialized) {
    return null;
  }

  return (
    <EmployeeModeContext.Provider
      value={{
        selectedEmployeeId,
        setSelectedEmployeeId,
      }}
    >
      {children}
    </EmployeeModeContext.Provider>
  );
};

export const useEmployeeMode = (): EmployeeModeContextType => {
  const context = useContext(EmployeeModeContext);
  if (context === undefined) {
    throw new Error(
      'useEmployeeMode must be used within an EmployeeModeProvider'
    );
  }
  return context;
};
